import { Link } from 'react-router-dom';
import { ArrowRight, Calculator } from 'lucide-react';
import SEO from '@/components/common/SEO';
import { ObangsaekStripe } from '@/components/editorial';
import { CALCULATORS } from '@/content/financial/calculators';

const BASE_URL = 'https://koreanhub.net';

export default function Calculators() {
  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Financial calculators',
    url: `${BASE_URL}/financial/calculators`,
    hasPart: CALCULATORS.map((c) => ({
      '@type': 'WebApplication',
      name: c.name,
      description: c.summary,
      applicationCategory: 'FinanceApplication',
      url: `${BASE_URL}/financial/calculators/${c.slug}`,
    })),
  };

  return (
    <div className="bg-white dark:bg-surface-dark">
      <SEO
        title="Financial Calculators — Underwrite a Small Business"
        description="Cash-on-cash return, micro-business valuation, side-hustle break-even, and a solo tax estimator. Free, interactive, and honest about where each number misleads."
        path="/financial/calculators"
        jsonLd={collectionSchema}
      />

      <header className="border-b border-ink-200 bg-ink-50 dark:border-surface-dark-border dark:bg-white/[0.02]">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
          <nav aria-label="Breadcrumb" className="mb-3 text-sm text-ink-500 dark:text-ink-400">
            <Link to="/financial" className="hover:underline">
              Financial
            </Link>
            <span className="mx-2" aria-hidden="true">
              /
            </span>
            <span className="text-ink-700 dark:text-ink-200">Calculators</span>
          </nav>
          <h1 className="text-3xl font-bold leading-tight text-ink-900 dark:text-ink-50 sm:text-4xl">
            Calculators
          </h1>
          <p lang="ko" className="mt-1 font-korean text-lg text-ink-500 dark:text-ink-400">
            계산기
          </p>
          <p className="mt-4 max-w-2xl text-base leading-7 text-ink-700 dark:text-ink-200">
            Four tools for the decisions that actually cost money: what a leveraged acquisition
            yields, what a small business is worth, when a side project breaks even, and whether an
            S-corp election pays for itself. Each one states where its own output misleads.
          </p>
        </div>
      </header>

      <ObangsaekStripe />

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {CALCULATORS.map((calc) => (
            <Link
              key={calc.slug}
              to={`/financial/calculators/${calc.slug}`}
              className="group flex flex-col rounded-xl border border-ink-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md dark:border-surface-dark-border dark:bg-surface-dark-card"
            >
              <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-cheong-500/10 text-cheong-500 dark:text-cheong-dark">
                <Calculator className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="text-lg font-bold text-ink-900 dark:text-ink-50">{calc.name}</h2>
              <p lang="ko" className="mt-0.5 font-korean text-xs text-ink-400" aria-hidden="true">
                {calc.nameKo}
              </p>
              <p className="mt-3 flex-1 text-sm leading-6 text-ink-500 dark:text-ink-400">
                {calc.summary}
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-cheong-500 group-hover:gap-2 dark:text-cheong-dark [transition:gap_150ms_ease-out]">
                Open calculator
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>

        <p className="my-10 rounded-xl border border-ink-200 bg-ink-50 p-5 text-sm leading-relaxed text-ink-700 dark:border-surface-dark-border dark:bg-white/[0.03] dark:text-ink-200">
          Every calculator returns an estimate derived only from the figures you enter. They omit
          working-capital swings, capex timing, seasonality, and local regulation — they are
          decision-support tools for learning, not underwriting systems.{' '}
          <Link
            to="/financial/disclosures"
            className="font-medium text-cheong-500 underline dark:text-cheong-dark"
          >
            Full disclosures
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
